// The order marker (2026-10-26, branch controls): where an order lands, two rings close in on the spot and a dot stays a
// moment, gold for attack-move (fight on the way, the default) and ivory for a move that walks past fights. The HUD's
// own gold and ivory (index.html --gold, --ivory), so the ground answers in the same voice as the buttons.
import * as THREE from 'three';

const KINDS = { fight: 0xf2c443, move: 0xfffaf0 } as const;
export type MarkKind = keyof typeof KINDS;
/** Seconds a marker shows; rings close from OPEN to SHUT radius (m). */
const LIFE = 0.75, OPEN = 2.6, SHUT = 0.7, POOL = 4;

type Mark = { g: THREE.Group; rings: THREE.Mesh[]; dot: THREE.Mesh; t: number };

export class OrderMarker {
  readonly root = new THREE.Group();
  private marks: Mark[] = [];
  private next = 0;

  constructor() {
    const ring = new THREE.RingGeometry(0.86, 1, 40), disc = new THREE.CircleGeometry(0.32, 24);
    for (let k = 0; k < POOL; k++) {
      const g = new THREE.Group();
      const mat = () => new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, toneMapped: false });
      const rings = [0, 1].map(() => { const m = new THREE.Mesh(ring, mat()); m.rotation.x = -Math.PI / 2; m.renderOrder = 3; g.add(m); return m; });
      const dot = new THREE.Mesh(disc, mat()); dot.rotation.x = -Math.PI / 2; dot.renderOrder = 3; g.add(dot);
      g.visible = false; this.root.add(g);
      this.marks.push({ g, rings, dot, t: 0 });
    }
  }

  /** An order landed at (x, z), y above the ground (a bridge deck lifts it). */
  show(x: number, z: number, y: number, kind: MarkKind) {
    const m = this.marks[this.next++ % POOL];
    m.t = LIFE; m.g.visible = true; m.g.position.set(x, y + 0.09, z);
    for (const o of [...m.rings, m.dot]) (o.material as THREE.MeshBasicMaterial).color.setHex(KINDS[kind]);
  }

  update(dt: number) {
    for (const m of this.marks) {
      if (m.t <= 0) continue;
      m.t -= dt;
      if (m.t <= 0) { m.g.visible = false; continue; }
      const u = 1 - m.t / LIFE;
      m.rings.forEach((r, k) => {
        const v = Math.min(1, Math.max(0, (u - k * 0.18) / 0.6)), e = 1 - (1 - v) ** 3; // ease out, the second a beat late
        r.scale.setScalar(OPEN + (SHUT - OPEN) * e);
        (r.material as THREE.MeshBasicMaterial).opacity = v <= 0 ? 0 : 0.95 * (1 - Math.max(0, u - 0.55) / 0.45);
      });
      (m.dot.material as THREE.MeshBasicMaterial).opacity = Math.min(1, u * 4) * (1 - Math.max(0, u - 0.6) / 0.4);
    }
  }

  /** How many markers show (the UI walk checks an order put one down). */
  get showing() { return this.marks.filter((m) => m.t > 0).length; }
}
